import { Link } from 'react-router-dom';
import Logo from '../../components/Logo';

export default function TermsOfUse() {
  return (
    <div className="min-h-screen bg-surface">
      <nav className="border-b border-outline-variant/20 bg-surface/80 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="hover:opacity-80 transition-opacity">
            <Logo className="h-9 text-primary" />
          </Link>
          <Link to="/auth" className="text-xs font-bold uppercase tracking-widest text-primary hover:opacity-80 transition-opacity">Open Calculator</Link>
        </div>
      </nav>

      <main className="max-w-3xl mx-auto px-6 py-16">
        <p className="text-xs font-bold uppercase tracking-widest text-on-surface-variant mb-4">Legal</p>
        <h1 className="text-4xl font-headline font-bold text-primary mb-4">Terms of Use</h1>
        <p className="text-sm text-on-surface-variant mb-2">Version 1.2 — Effective 13 April 2026</p>
        <p className="text-sm text-on-surface-variant mb-12">These terms are subject to change. The current version and effective date are shown above.</p>

        <div className="prose prose-sm max-w-none space-y-10 font-body text-on-surface">

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-4">1. General information only</h2>
            <p className="leading-relaxed text-on-surface-variant">
              This website and the tools it provides (including the feasibility calculator) are for general informational and scenario-modelling purposes only. Nothing on this site constitutes legal, financial, tax, accounting, valuation, planning, engineering, building, quantity surveying, lending, or development advice.
            </p>
            <p className="leading-relaxed text-on-surface-variant mt-3">
              You must not treat any output, result, or content from this site as a substitute for independent professional advice appropriate to your circumstances.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-4">2. Scenario modelling only</h2>
            <p className="leading-relaxed text-on-surface-variant">
              The calculator is provided as a scenario-modelling tool only. Outputs are generated based on user inputs, assumed default values, and available data sources at the time of calculation. Results are indicative only and depend on assumptions, user inputs, market conditions, planning controls, construction costs, finance availability, taxes, consultant advice, and other variables that may change without notice.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-4">3. No representation of suitability</h2>
            <p className="leading-relaxed text-on-surface-variant">
              We do not represent that any output is accurate, complete, current, fit for purpose, lender-acceptable, planning-compliant, or suitable for your particular circumstances, project, or objectives. Individual results will vary based on site-specific conditions, finance arrangements, market movements, planning determinations, and other factors outside our control or knowledge.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-4">4. No warranty</h2>
            <p className="leading-relaxed text-on-surface-variant">
              To the maximum extent permitted by law, Site Yield Pty Ltd makes no warranty — express or implied — as to the accuracy, completeness, currency, reliability, fitness for purpose, lender acceptance, planning compliance, or profitability of any output, content, or data provided through this site. Data sourced from third parties (including government GIS repositories) is provided as-is and may not reflect the current position.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-4">5. User responsibility</h2>
            <p className="leading-relaxed text-on-surface-variant">
              You are responsible for:
            </p>
            <ul className="list-disc pl-6 mt-3 space-y-2 text-on-surface-variant">
              <li>the accuracy of all inputs you provide;</li>
              <li>selecting assumptions appropriate to your specific project;</li>
              <li>independently verifying all outputs, constraints, planning controls, costs, finance terms, tax obligations, and other matters before acting;</li>
              <li>obtaining independent professional advice (including from a town planner, quantity surveyor, valuer, mortgage broker, accountant, and property lawyer) before making any decision.</li>
            </ul>
          </section>

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-4">6. No reliance as sole or primary basis</h2>
            <p className="leading-relaxed text-on-surface-variant">
              This tool must not be relied on as the sole or primary basis for any acquisition, investment, lending, pricing, feasibility assessment, or development decision. You must conduct your own due diligence and obtain independent professional advice appropriate to your circumstances before acting on any output.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-4">7. Mandatory independent advice</h2>
            <p className="leading-relaxed text-on-surface-variant">
              Before acting on any output from this site, you must independently confirm planning controls, build costs, consultant fees, taxes, finance terms, sales evidence, and valuation assumptions with qualified advisers including, as appropriate: a licensed town planner, a registered quantity surveyor or builder, a licensed real estate valuer, a mortgage broker or lender, a registered accountant or tax adviser, and a qualified property lawyer.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-4">8. Exclusion of liability</h2>
            <p className="leading-relaxed text-on-surface-variant">
              To the maximum extent permitted by law, Site Yield Pty Ltd excludes all liability for loss or damage — direct, indirect, consequential, special, or otherwise — arising from or in connection with your use of or reliance on this site, the calculator, or any output, including liability arising from negligence.
            </p>
            <p className="leading-relaxed text-on-surface-variant mt-3">
              Where liability cannot lawfully be excluded, our liability is limited, at our election, to resupply of the service or payment of the cost of resupply of the service, or, where a fee was paid, to the amount paid by you for access to this site in the 12 months immediately before the claim arose, or AUD 100 if no fee was paid.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-4">9. Non-excludable rights</h2>
            <p className="leading-relaxed text-on-surface-variant">
              Nothing in these terms excludes, restricts, or modifies any right or remedy, or any guarantee, warranty, or other term or condition, implied or imposed by any law (including the Australian Consumer Law) where doing so would be unlawful.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-4">10. Data and privacy</h2>
            <p className="leading-relaxed text-on-surface-variant">
              We collect and handle personal data in accordance with our <Link to="/legal/privacy-policy" className="text-primary underline">Privacy Policy</Link>. By using this site you consent to that collection and handling. Project data you enter into the calculator may be stored to support your sessions and service delivery. We do not share your project inputs with third parties for commercial purposes without your consent.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-4">11. Governing law</h2>
            <p className="leading-relaxed text-on-surface-variant">
              These terms are governed by the laws of Victoria, Australia. You submit to the non-exclusive jurisdiction of the courts of Victoria and courts of appeal from those courts.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-headline font-bold text-primary mb-4">12. Terms updates</h2>
            <p className="leading-relaxed text-on-surface-variant">
              We may update these terms from time to time. The current version number and effective date are displayed at the top of this page. Continued use of the site after an update constitutes acceptance of the revised terms. Material changes will be notified by email where possible.
            </p>
          </section>

          <section className="bg-amber-50 border border-amber-200 rounded-lg p-6">
            <p className="text-sm font-bold text-amber-900 mb-2">Important notice</p>
            <p className="text-sm text-amber-800 leading-relaxed">
              These terms have been drafted for general use and have not been reviewed by an Australian lawyer for your specific circumstances. Site Yield Pty Ltd recommends that you obtain independent legal advice if you have any questions about these terms or your legal position.
            </p>
          </section>

        </div>
      </main>

      <footer className="border-t border-outline-variant/20 py-8 px-6">
        <div className="max-w-3xl mx-auto flex flex-wrap gap-4 text-xs text-on-surface-variant">
          <Link to="/legal/privacy-policy" className="hover:text-primary transition-colors">Privacy Policy</Link>
          <Link to="/legal/terms-of-use" className="hover:text-primary transition-colors font-bold text-primary">Terms of Use</Link>
          <Link to="/legal/cookie-notice" className="hover:text-primary transition-colors">Cookie Notice</Link>
          <Link to="/legal/output-disclaimer" className="hover:text-primary transition-colors">Output Disclaimer</Link>
        </div>
      </footer>
    </div>
  );
}
